import { isNonAuthRoute } from 'src/latest/src/constants/AppRoutesExpressions';
import {
  AGREEMENT_SENTENCES,
  DOCUMENT_LABELS,
  TermsAcceptance,
  TermsDocument,
  TermsDocumentType,
} from './TermsAcceptanceConstant';

export type AgreementPart =
  | { text: string }
  | { document: TermsDocumentType; label: string; url: string };

const isWebUrl = (url?: string) => !!url && /^https?:\/\//i.test(url);

/**
 * Fills the variant's agreement sentence with links from `documents`.
 * @param {TermsAcceptance} termsAcceptance the user's metadata.terms_acceptance
 * @returns {Array|null} the sentence parts, or null when the payload can't
 * render the copy (unknown variant, or a linked document missing its url)
 */
export const buildAgreementParts = (
  termsAcceptance?: TermsAcceptance | null,
): AgreementPart[] | null => {
  const sentence = termsAcceptance?.variant
    ? AGREEMENT_SENTENCES[termsAcceptance.variant]
    : undefined;

  if (!sentence) return null;

  const parts: AgreementPart[] = sentence.map(segment => {
    if (typeof segment === 'string') return { text: segment };

    const url = termsAcceptance?.documents?.find(
      doc => doc.document_type === segment.document,
    )?.url;

    return {
      document: segment.document,
      label: DOCUMENT_LABELS[segment.document],
      url: url || '',
    };
  });

  const hasEveryLink = parts.every(
    part => !('url' in part) || isWebUrl(part.url),
  );

  return hasEveryLink ? parts : null;
};

/**
 * @param {Array} documents metadata.terms_acceptance.documents
 * @returns {Array} the request body's documents
 */

export const toAcceptedDocuments = (
  documents: TermsDocument[] = [],
): Pick<TermsDocument, 'document_type' | 'document_version'>[] =>
  documents.map(doc => ({
    document_type: doc.document_type,
    document_version: doc.document_version,
  }));

/**
 * @param {String} pathname the current route
 * @returns {Boolean} true when the modal stays hidden on this route
 */

export const isTermsAcceptanceExemptRoute = (pathname: string) =>
  isNonAuthRoute(pathname) || /^\/profile\//.test(pathname);

export const shouldShowTermsAcceptance = ({
  isAuthed,
  termsAcceptance,
  pathname,
}: {
  isAuthed: boolean;
  termsAcceptance?: TermsAcceptance | null;
  pathname: string;
}) =>
  isAuthed &&
  termsAcceptance?.eligible === true &&
  !isTermsAcceptanceExemptRoute(pathname);
